import React, { useMemo, useEffect, useCallback, useRef } from 'react'

import PropTypes from 'prop-types'

import {
	removeFormatter,
	formatCPF,
	formatCep,
	formatCreditCardNumber,
	formatCreditCardExpirationDate,
	formatState,
	formatCVV,
	formatDate,
	formatCardName,
	formatAddressNumber,
	formatCellPhone,
	formatCNPJ,
	formatCRP,
	formatYear,
	formatName,
	formatNoMask,
	formatNumberInteger
} from '../../../utils/formattersHookForm'

import { Input } from './Input'

const CPF = 'cpf'
const CEP = 'cep'
const CREDIT_CARD_NUMBER = 'credit_card_number'
const CREDIT_CARD_EXPIRATION_DATE = 'credit_card_expiration_date'
const CVV = 'cvv'
const STATE = 'state'
const DATE = 'date'
const CARD_NAME = 'card_name'
const NO_MASK = 'no_mask'
const ADDRESS_NUMBER = 'address_number'
const CELL_PHONE = 'cell_phone'
const CNPJ = 'cnpj'
const CRP = 'crp'
const YEAR = 'year'
const NAME = 'name'
const NUMBER_INTEGER = 'number_integer'

const MaskedInput = ({
	name,
	register,
	onChange: onChangeProp,
	onBlur: onBlurProp,
	mask,
	value: valueProp,
	disabled,
	...props
}) => {
	const inputRef = useRef(null)

	const isV7Spread = register && typeof register === 'object' && 'ref' in register
	const isV5Ref = register && typeof register === 'function'
	
	const currentValue = isV7Spread 
		? (register.value ?? valueProp ?? '')
		: (valueProp ?? '')

	const finalOnChange = isV7Spread ? register.onChange : onChangeProp
	
	const finalOnBlur = isV7Spread ? register.onBlur : onBlurProp

	const maskedRegex = useMemo(() => {
		switch (mask) {
			case CEP:
				return formatCep
			case CPF:
				return formatCPF
			case CREDIT_CARD_NUMBER:
				return formatCreditCardNumber
			case CREDIT_CARD_EXPIRATION_DATE:
				return formatCreditCardExpirationDate
			case STATE:
				return formatState
			case CVV:
				return formatCVV
			case DATE:
				return formatDate
			case CARD_NAME:
				return formatCardName
			case ADDRESS_NUMBER:
				return formatAddressNumber
			case CELL_PHONE:
				return formatCellPhone
			case CNPJ:
				return formatCNPJ
			case CRP:
				return formatCRP
			case YEAR:
				return formatYear
			case NAME:
				return formatName
			case NUMBER_INTEGER:
				return formatNumberInteger
			case NO_MASK:
				return formatNoMask
			default:
				return (value) => value
		}
	}, [mask])

	useEffect(() => {
		if (inputRef.current && maskedRegex) {
			const formattedValue = !currentValue 
				? ''
				: mask === 'card_name' || mask === 'no_mask'
					? maskedRegex(currentValue)
					: maskedRegex(removeFormatter(currentValue))
			
			if (inputRef.current.value !== formattedValue) {
				inputRef.current.value = formattedValue
			}
		}
	}, [currentValue, maskedRegex, mask])

	const handleChange = useCallback(
		(event) => {
			const newValue = event.target.value
			const fieldName = event.target.name

			const parsedValue =
				mask === 'card_name' || mask === 'no_mask'
					? maskedRegex(newValue)
					: maskedRegex(removeFormatter(newValue))

			if (!parsedValue && newValue) {
				if (inputRef.current) {
					inputRef.current.value = currentValue || ''
				}
				return
			}

			const nextValue = parsedValue || ''
			
			if (inputRef.current) {
				inputRef.current.value = nextValue
			}

			if (finalOnChange) {
				finalOnChange({ 
					target: { value: nextValue, name: fieldName } 
				})
			}
		},
		[mask, maskedRegex, currentValue, finalOnChange]
	)

	const handleBlur = useCallback(
		(event) => {
			if (finalOnBlur) {
				finalOnBlur(event)
			}
		},
		[finalOnBlur]
	)

	const finalRef = isV7Spread ? register.ref : (isV5Ref ? register : inputRef)

	return (
		<Input
			disabled={disabled}
			value={currentValue}
			onChange={handleChange}
			onBlur={handleBlur}
			name={name}
			register={finalRef}
			{...props}
		/>
	)
}

MaskedInput.propTypes = {
	name: PropTypes.string.isRequired,
	register: PropTypes.func,
	onChange: PropTypes.func,
	mask: PropTypes.oneOf([
		CPF,
		CEP,
		CREDIT_CARD_NUMBER,
		CREDIT_CARD_EXPIRATION_DATE,
		STATE,
		CVV,
		DATE,
		CARD_NAME,
		ADDRESS_NUMBER,
		CELL_PHONE,
		CNPJ,
		CRP,
		YEAR,
		NAME,
		NUMBER_INTEGER,
		NO_MASK
	]).isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export { MaskedInput }
